import {
	QueuesWithResourcesActions,
	QueuesWithResourcesState,
	useQueuesWithResourcesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { notificationListener } from "@/hooks/notification";
import { ServerEvent } from "@/common/notification";
import { useAuth } from "@/hooks/authentication";
import { useEffect } from "react";
import { fetchMemberedQueuesWithResources } from "@/repository/queryFns";
import { CallSession } from "@/common/models/callSession";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import {
	useCurrentInteractionStore,
	CurrentInteractionState,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { AutoOpenTrigger, QueueWithResources } from "@/repository/models";
import { cleanupResourcesCache } from "@/common/helpers";
import { openResourcesPopup } from "@/common/helpers/popupHelper";

export const useResourcesManager = () => {
	const authContext = useAuth();
	const queueWithResourcesStore = useQueuesWithResourcesStore(
		(store: QueuesWithResourcesState & QueuesWithResourcesActions) => store
	);

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	useEffect(() => {
		if (!authContext.isLogged) {
			return;
		}

		refreshQueuesWithResources(currentInteraction?.id ?? "");
	}, [authContext.isLogged, currentInteraction]);

	notificationListener(ServerEvent.DynamicUrlsChanged, () => {
		refreshQueuesWithResources(currentInteraction?.id ?? "");
	});

	notificationListener(
		ServerEvent.CallSessionStateChanged,
		(callSession: CallSession) => {
			if (callSession.state !== AgentInteractionStatus.Terminated) {
				return;
			}

			refreshQueuesWithResources(currentInteraction?.id ?? "");
		}
	);

	notificationListener(
		ServerEvent.CallSessionStateChanged,
		(callSession: CallSession) => {
			openResources(callSession);
		}
	);

	const openResources = (callSession: CallSession) => {
		const queue = queueWithResourcesStore.getQueueById(callSession.queueId);

		switch (callSession.state) {
			case AgentInteractionStatus.Offering:
			case AgentInteractionStatus.ReceivingWarmTransferInvitation:
				if (
					appStoreCache[CacheKey.OfferingDynamicUrlCallId] === callSession.id
				) {
					return;
				}

				appStoreActions.setCache(
					CacheKey.OfferingDynamicUrlCallId,
					callSession.id
				);

				if (callSession.isColdTransferLeave) {
					openResourcesPopup(queue, AutoOpenTrigger.WhenClosed);
					appStoreActions.removeCaches([CacheKey.OfferingDynamicUrlCallId]);
					
					return;
				}

				cleanupResourcesCache(callSession, appStoreActions);
				refetchResourcesAndOpenPopup(queue, callSession?.queueId);
				break;
			case AgentInteractionStatus.Terminated:
				if (
					!callSession.hasBeenHandled ||
					(callSession.hasBeenHandled && callSession.isColdTransferRejected) ||
					callSession.isFallback
				) {
					return;
				}

				if (appStoreCache[CacheKey.RemoveDynamicUrlCallId] === callSession.id) {
					return;
				}

				appStoreActions.setCache(
					CacheKey.RemoveDynamicUrlCallId,
					callSession.id
				);

				cleanupResourcesCache(callSession, appStoreActions);
				openResourcesPopup(queue, AutoOpenTrigger.WhenClosed);
				break;
		}
	};

	const refetchResourcesAndOpenPopup = (
		queue: QueueWithResources | undefined,
		interactionQueueId: string | undefined
	) => {
		fetchMemberedQueuesWithResources(currentInteraction?.id ?? "")
			.then((queues: QueueWithResources[]) => {
				openResourcesPopup(

					queues.find((q) => q.id === interactionQueueId),
					AutoOpenTrigger.WhenOffering
				);

				queueWithResourcesStore.setQueues(queues);
			})
			.catch((_) => {
				openResourcesPopup(queue, AutoOpenTrigger.WhenOffering);
			});
	};

	const refreshQueuesWithResources = (currentInteractionId: string) =>
		fetchMemberedQueuesWithResources(currentInteractionId).then(
			queueWithResourcesStore.setQueues
		);
};
